import { graphql } from "gatsby";
import {
  BrowserSupportHelper,
  HeroBlock as CoreHeroBlock,
  ExistingThemes,
  SimplifyGatsbyQuery,
  useUiBkgVideoWorkaround,
  withLivePreview,
  EventAggregator,
  PrimaryButtonLink,
  heroBlockLivePreviewConfig,
} from "@parksandresorts/core";
import { ComponentProps } from "react";
import Parallax from "../../block-elements/parallax/parallax";
import UiPicture from "@rnb/ui-picture";
import UiBkgVideo from "@rnb/ui-bkg-video";
import styles from "./hero-block.module.less";
import LinkWrapper from "components/block-elements/link-wrapper/link-wrapper";
import { getHeroPicture } from "components/helpers/picture-helper";

const breakPointSmallDevice = 576;

const OldHeroBlock = (props: SimplifyGatsbyQuery<Queries.HeroBlockFragment>) => {
  const videoKey = useUiBkgVideoWorkaround(breakPointSmallDevice);

  const imageUrlForSmallDevice = props.heroImageSmallDevice?.file?.url;
  const imageUrlForLargeDevice = props.heroImageLargeDevice?.file?.url;
  const buttonLinkInternalLink = props.buttonLink?.internalLink?.slug;
  const buttonLinkExternalLink = props.buttonLink?.externalUrl;
  const videoUrl = props.backgroundVideo?.file?.url;
  const videoUrlMobile = props.backgroundVideoMobile?.file?.url;

  const isInternetExplorerOrEdge = BrowserSupportHelper.checkIsBrowserEdge() || BrowserSupportHelper.checkIsBrowserIE();

  const actualButtonLink = buttonLinkExternalLink || buttonLinkInternalLink || "";

  const smallDevice = props.heroImageSmallDevice;
  const largeDevice = props.heroImageLargeDevice;

  const content = (
    <>
      <TextAndDecoration
        textPositioning={props.textPositioning}
        entryTitle={props.entryTitle}
        enableTextBackground={props.enableTextBackground}
        heroParagraph={props.heroParagraph}
        buttonLabel={props.buttonLabel}
        buttonLink={actualButtonLink}
      />
      <div className={styles.hero__gradientLayer} />
    </>
  );

  return (
    <section
      className={["block block--no-margin", props.componentTheme ? `component-theme-${props.componentTheme}` : ""].join(
        " ",
      )}
      data-contentful-id={props.contentful_id}
    >
      {videoUrl ? (
        <Parallax parallaxOffset={64}>
          <UiBkgVideo
            key={videoKey}
            wrapperExtraClasses={styles.hero__videoWrapper}
            videoExtraClasses={[
              styles.hero__video,
              "data-block-in-nav-parallax",
              isInternetExplorerOrEdge ? styles.hero__video_ie : styles.hero__video_normal,
            ].join(" ")}
            srcDesktop={videoUrl}
            srcMobile={videoUrlMobile || undefined}
            autoplay={true}
            muted={true}
            preload="auto"
            playsinline={true}
            loop={true}
            breakPointSmallDevice={breakPointSmallDevice}
            posterSmallDevice={imageUrlForSmallDevice ? imageUrlForSmallDevice + "?q=30&fm=webp" : undefined}
            posterLargeDevice={imageUrlForLargeDevice ? imageUrlForLargeDevice + "?q=30&fm=webp" : undefined}
            videoPosterExtraClass={styles.hero__videoPoster}
            videoPosterHiddenClass={styles.hero__videoPosterHidden}
          >
            {content}
          </UiBkgVideo>
        </Parallax>
      ) : (
        <Parallax parallaxOffset={200}>
          <div>
            <UiPicture
              {...getHeroPicture(smallDevice, largeDevice, `${props.contentful_id}_hero`)}
              useLowQualityImage={true}
              classNamePictureWrapper={styles.heroPictureWrapper}
            />
            {content}
          </div>
        </Parallax>
      )}
    </section>
  );
};

const HeroBlock = (props: SimplifyGatsbyQuery<Queries.HeroBlockFragment>) => {
  if (!props.useNewHeroBlock) {
    return <OldHeroBlock {...props} />;
  }
  type CoreHeroBlockProps = ComponentProps<typeof CoreHeroBlock>;
  type T_SHIRT_FROM_CORE = NonNullable<CoreHeroBlockProps["firstTitle"]>["size"];

  const {
    contentful_id,
    heroImageSmallDevice,
    heroImageLargeDevice,
    backgroundVideo,
    backgroundVideoMobile,
    componentTheme,
    buttonLink,
    buttonLabel,
    size,
    useFirstTitle,
    firstTitle,
    firstTitleSize,
    firstTitleTiltDegrees,
    firstTitleBackground,
    firstTitleHasDropShadow,
    useSecondTitle,
    secondTitle,
    secondTitleSize,
    secondTitleTiltDegrees,
    secondTitleBackground,
    secondTitleHasDropShadow,
    textAlignment,
    contentPosition,
    logoImage,
    logoPosition,
    repeatingPattern,
    repeatingPatternOpacity,
    customColors,
  } = props;

  return (
    <CoreHeroBlock
      contentfulId={contentful_id}
      nextBlockTypesBlockingPausePlay={["ContentfulLink", "ContentfulRestaurantBlock"]}
      componentTheme={componentTheme as ExistingThemes}
      textAlignment={textAlignment as CoreHeroBlockProps["textAlignment"]}
      contentPosition={contentPosition as CoreHeroBlockProps["contentPosition"]}
      colorOverrides={{
        background: customColors?.backgroundColor,
        pausePlayButtonBackground: customColors?.iconBackgroundColor,
        pausePlayButtonIcon: customColors?.iconTextColor,
        buttonBackground: customColors?.buttonBackgroundColor,
        buttonText: customColors?.buttonTextColor,
      }}
      callToActionButton={{
        ...buttonLink,
        title: buttonLabel,
      }}
      size={size as CoreHeroBlockProps["size"]}
      imageMobile={
        heroImageSmallDevice
          ? { image: heroImageSmallDevice.gatsbyImageData, altText: heroImageSmallDevice.description }
          : undefined
      }
      imageDesktop={
        heroImageLargeDevice
          ? { image: heroImageLargeDevice.gatsbyImageData, altText: heroImageLargeDevice.description }
          : undefined
      }
      videoMobile={
        backgroundVideoMobile?.file?.url
          ? {
              file: { url: backgroundVideoMobile?.file?.url, contentType: "video/mp4" },
              altText: "text",
              poster: heroImageSmallDevice?.file?.url,
            }
          : undefined
      }
      videoDesktop={
        backgroundVideo?.file?.url
          ? {
              file: { url: backgroundVideo?.file?.url, contentType: "video/mp4" },
              altText: "text",
              poster: heroImageLargeDevice?.file?.url,
            }
          : undefined
      }
      logo={
        logoImage
          ? {
              imageMobile: { image: logoImage?.mobile, altText: logoImage.description },
              imageDesktop: { image: logoImage?.desktop, altText: logoImage.description },
              position: logoPosition as NonNullable<CoreHeroBlockProps["logo"]>["position"],
            }
          : undefined
      }
      repeatingPatternImageUrls={
        repeatingPattern
          ? {
              highQualityMobile: repeatingPattern.highQualityMobile?.images.fallback?.src ?? "",
              lowQualityMobile: repeatingPattern.lowQualityMobile?.images.fallback?.src ?? "",
              highQualityDesktop: repeatingPattern.highQualityDesktop?.images.fallback?.src ?? "",
              lowQualityDesktop: repeatingPattern.lowQualityDesktop?.images.fallback?.src ?? "",
            }
          : undefined
      }
      repeatingPatternOpacity={repeatingPatternOpacity}
      firstTitle={
        firstTitle && useFirstTitle !== "no"
          ? {
              text: firstTitle,
              size: firstTitleSize as T_SHIRT_FROM_CORE,
              withBackground: firstTitleBackground,
              effect: {
                tiltDegrees: firstTitleTiltDegrees,
                dropShadow: firstTitleHasDropShadow,
              },
              colorOverrides: {
                background: customColors?.firstTitleBackgroundColor,
                text: customColors?.firstTitleTextColor,
                dropShadow: customColors?.firstTitleDropShadowColor,
              },
            }
          : undefined
      }
      secondTitle={
        secondTitle && useSecondTitle !== "no"
          ? {
              text: secondTitle,
              size: secondTitleSize as T_SHIRT_FROM_CORE,
              withBackground: secondTitleBackground,
              effect: {
                tiltDegrees: secondTitleTiltDegrees,
                dropShadow: secondTitleHasDropShadow,
              },
              colorOverrides: {
                background: customColors?.secondTitleBackgroundColor,
                text: customColors?.secondTitleTextColor,
                dropShadow: customColors?.secondTitleDropShadowColor,
              },
            }
          : undefined
      }
    />
  );
};

function TextAndDecoration(
  props: Pick<
    Queries.HeroBlockFragment,
    "textPositioning" | "entryTitle" | "enableTextBackground" | "heroParagraph" | "buttonLabel"
  > & { buttonLink: string },
) {
  const { textPositioning, entryTitle, enableTextBackground, heroParagraph, buttonLabel, buttonLink } = props;

  const linkWrapperUrl = buttonLink && !buttonLabel ? buttonLink : null;

  return (
    <LinkWrapper url={linkWrapperUrl}>
      <div className={styles.hero__infoOuterwrapper}>
        <div className={[styles.hero__infoInnerwrapper, styles[textPositioning ?? ""]].join(" ")}>
          {entryTitle && (
            <h1
              className={[
                styles.hero__header,
                enableTextBackground ? styles.hero__colorText : styles.hero__textShadow,
                "hero-heading",
              ].join(" ")}
            >
              {entryTitle}
            </h1>
          )}
          {heroParagraph && (
            <p className={[styles.hero__paragraph, enableTextBackground ? "" : styles.hero__textShadow].join(" ")}>
              <span className={enableTextBackground ? styles.hero__paragraphBackground : ""}>{heroParagraph}</span>
            </p>
          )}
          {buttonLabel && buttonLink && (
            <span
              className={styles.buttonWrapper}
              onClick={() => {
                EventAggregator.publish("trackEvent", {
                  category: "CTA Click",
                  action: buttonLabel,
                  label: buttonLink,
                });
              }}
            >
              <PrimaryButtonLink label={buttonLabel} className={styles.ctaButton} url={buttonLink} />
            </span>
          )}
        </div>
      </div>
    </LinkWrapper>
  );
}

export default withLivePreview(HeroBlock, heroBlockLivePreviewConfig);

export const fragmentHeroBlock = graphql`
  fragment HeroBlock on ContentfulHeroBlock {
    ... on ContentfulHeroBlock {
      contentful_id
      id
      entryTitle
      heroParagraph
      paragraphHasBackground
      buttonLabel
      textPositioning
      enableTextBackground
      componentTheme
      useNewHeroBlock
      useFirstTitle
      firstTitle {
        raw
      }
      firstTitleSize
      firstTitleTiltDegrees
      firstTitleHasDropShadow
      firstTitleBackground
      useSecondTitle
      secondTitle {
        raw
      }
      secondTitleSize
      secondTitleTiltDegrees
      secondTitleHasDropShadow
      secondTitleBackground
      textAlignment
      contentPosition
      size
      buttonLink {
        ...Link
      }
      heroImageSmallDevice {
        ...Asset
        gatsbyImageData(aspectRatio: 1, quality: 70, resizingBehavior: FILL)
      }
      logoImage {
        description
        mobile: gatsbyImageData(width: 50, resizingBehavior: FILL)
        desktop: gatsbyImageData(width: 100, resizingBehavior: FILL)
      }
      logoPosition
      heroImageLargeDevice {
        ...Asset
        gatsbyImageData(aspectRatio: 3, quality: 70, resizingBehavior: FILL)
        ultraWideBlur: gatsbyImageData(aspectRatio: 4, placeholder: BLURRED)
      }
      backgroundVideo {
        file {
          url
        }
      }
      backgroundVideoMobile {
        file {
          url
        }
      }
      repeatingPattern {
        highQualityMobile: gatsbyImageData(width: 150, quality: 80, resizingBehavior: FILL)
        lowQualityMobile: gatsbyImageData(width: 50, quality: 50, resizingBehavior: FILL)
        highQualityDesktop: gatsbyImageData(width: 300, quality: 80, resizingBehavior: FILL)
        lowQualityDesktop: gatsbyImageData(width: 100, quality: 50, resizingBehavior: FILL)
        file {
          url
        }
      }
      repeatingPatternOpacity
      customColors {
        backgroundColor
        iconBackgroundColor
        iconTextColor
        firstTitleBackgroundColor
        firstTitleTextColor
        firstTitleDropShadowColor
        secondTitleBackgroundColor
        secondTitleTextColor
        secondTitleDropShadowColor
        buttonBackgroundColor
        buttonTextColor
      }
    }
  }
`;
